import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router';
import { apiClient } from '../../lib/api-client.js';
import { useAuth } from '../auth/useAuth.js';
import { Spinner } from '../../components/Spinner.js';

interface BlockedUser {
  id: string;
  nickname: string;
  avatarUrl: string | null;
  blockedAt: string;
}

interface MessageSender {
  id: string;
  nickname: string;
  avatarUrl: string | null;
}

interface Message {
  id: string;
  body: string;
  senderId: string;
  readAt: string | null;
  createdAt: string;
  sender: MessageSender;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function Avatar({ url, nickname }: { url: string | null; nickname: string }) {
  if (url) {
    return (
      <img src={url} alt={nickname} className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
    );
  }
  return (
    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
      <span className="text-gray-500 text-sm font-semibold">{nickname[0]}</span>
    </div>
  );
}

export function ConversationView() {
  const { conversationId } = useParams<{ conversationId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { dbUser } = useAuth();

  const recipientId = searchParams.get('to');
  const isNewConversation = conversationId === 'new';

  const [messages, setMessages] = useState<Message[]>([]);
  const [partnerName, setPartnerName] = useState('');
  const [partnerId, setPartnerId] = useState<string | null>(null);
  const [loading, setLoading] = useState(!isNewConversation);
  const [sending, setSending] = useState(false);
  const [body, setBody] = useState('');
  const [isBlocked, setIsBlocked] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const fetchMessages = useCallback(async () => {
    if (!conversationId || isNewConversation) return;
    try {
      const data = await apiClient.get<Message[]>(`/messages/${conversationId}`, {
        requireAuth: true,
      });
      setMessages(data ?? []);
      if (data && data.length > 0) {
        const partner = data.find((m) => m.senderId !== dbUser?.id)?.sender;
        if (partner) {
          setPartnerName(partner.nickname);
          setPartnerId(partner.id);
        }
      }
      await apiClient.patch(`/messages/${conversationId}/read`, {}, { requireAuth: true });
      window.dispatchEvent(new CustomEvent('dm-read'));
    } catch {
      // エラー無視
    } finally {
      setLoading(false);
    }
  }, [conversationId, isNewConversation, dbUser?.id]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  useEffect(() => {
    const handleDmReceived = () => {
      void fetchMessages();
    };
    window.addEventListener('dm-received', handleDmReceived);
    return () => window.removeEventListener('dm-received', handleDmReceived);
  }, [fetchMessages]);

  // isNewConversation の場合は recipientId をパートナーIDとして使用
  useEffect(() => {
    if (isNewConversation && recipientId) {
      setPartnerId(recipientId);
    }
  }, [isNewConversation, recipientId]);

  // パートナーIDが確定したらブロック状態を確認
  useEffect(() => {
    if (!partnerId) return;
    apiClient
      .get<BlockedUser[]>('/blocks', { requireAuth: true })
      .then((data) => {
        if (data) setIsBlocked(data.some((b) => b.id === partnerId));
      })
      .catch(() => {});
  }, [partnerId]);

  // メニュー外クリックで閉じる
  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // 会話画面表示中は Layout の main スクロールを無効化
  useEffect(() => {
    const mainEl = document.querySelector('main') as HTMLElement | null;
    if (!mainEl) return;
    const prev = mainEl.style.overflow;
    mainEl.style.overflow = 'hidden';
    return () => {
      mainEl.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    if (loading || messages.length === 0 || !listRef.current) return;
    const el = listRef.current;
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [messages, loading]);

  const handleSend = async () => {
    if (!body.trim() || sending) return;

    const targetRecipientId = isNewConversation ? recipientId : null;
    if (isNewConversation && !targetRecipientId) return;

    setSending(true);
    try {
      if (isNewConversation && targetRecipientId) {
        const result = await apiClient.post<{ conversationId: string; message: Message }>(
          '/messages',
          { recipientId: targetRecipientId, body: body.trim() },
          { requireAuth: true }
        );
        navigate(`/messages/${result.conversationId}`, { replace: true });
      } else {
        await apiClient.post<{ conversationId: string; message: Message }>(
          '/messages',
          {
            recipientId: messages.find((m) => m.senderId !== dbUser?.id)?.senderId,
            body: body.trim(),
          },
          { requireAuth: true }
        );
        await fetchMessages();
      }
      setBody('');
    } catch {
      // エラー無視
    } finally {
      setSending(false);
    }
  };

  const handleBlock = async () => {
    if (!partnerId) return;
    const ok = window.confirm(
      `${partnerName} をブロックしますか？\nブロックすると、この会話がメッセージ一覧から消えます。`
    );
    if (!ok) return;
    setMenuOpen(false);
    try {
      await apiClient.post('/blocks', { userId: partnerId }, { requireAuth: true });
      navigate('/messages');
    } catch {
      alert('ブロックに失敗しました。もう一度お試しください。');
    }
  };

  const handleUnblock = async () => {
    if (!partnerId) return;
    setMenuOpen(false);
    try {
      await apiClient.delete(`/blocks/${partnerId}`, { requireAuth: true });
      setIsBlocked(false);
    } catch {
      alert('ブロック解除に失敗しました。もう一度お試しください。');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  const handleSendLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const geoBody = `geo:${pos.coords.latitude.toFixed(6)},${pos.coords.longitude.toFixed(6)}`;
        const targetRecipientId = isNewConversation
          ? recipientId
          : messages.find((m) => m.senderId !== dbUser?.id)?.senderId;
        if (!targetRecipientId) return;
        setSending(true);
        apiClient
          .post<{ conversationId: string; message: Message }>(
            '/messages',
            { recipientId: targetRecipientId, body: geoBody },
            { requireAuth: true }
          )
          .then(async (result) => {
            if (isNewConversation) {
              navigate(`/messages/${result.conversationId}`, { replace: true });
            } else {
              await fetchMessages();
            }
          })
          .catch(() => {})
          .finally(() => setSending(false));
      },
      () => alert('位置情報を取得できませんでした')
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }

  // 日付ごとにメッセージをグループ化
  const groupedMessages: { date: string; messages: Message[] }[] = [];
  for (const msg of messages) {
    const date = new Date(msg.createdAt).toDateString();
    const last = groupedMessages[groupedMessages.length - 1];
    if (last && last.date === date) {
      last.messages.push(msg);
    } else {
      groupedMessages.push({ date, messages: [msg] });
    }
  }

  return (
    <div className="flex flex-col max-w-2xl mx-auto overflow-hidden h-[calc(100dvh-4rem)]">
      {/* ヘッダー */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 bg-white sticky top-0 z-10">
        <button
          className="p-1 text-gray-500 hover:text-gray-700"
          onClick={() => navigate('/messages')}
          aria-label="戻る"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M15 18l-6-6 6-6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <span className="flex-1 font-semibold text-gray-900">
          {isNewConversation ? 'メッセージを送る' : partnerName || '会話'}
        </span>
        {!isNewConversation && partnerId && (
          <div className="relative" ref={menuRef}>
            <button
              className="p-1 text-gray-400 hover:text-gray-600"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="メニュー"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="5" r="1.5" />
                <circle cx="12" cy="12" r="1.5" />
                <circle cx="12" cy="19" r="1.5" />
              </svg>
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-8 w-40 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-20">
                {isBlocked ? (
                  <button
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => void handleUnblock()}
                  >
                    ブロック解除
                  </button>
                ) : (
                  <button
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    onClick={() => void handleBlock()}
                  >
                    ブロック
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* メッセージ一覧 */}
      <div
        ref={listRef}
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4 space-y-4"
      >
        {groupedMessages.map(({ date, messages: dayMessages }) => (
          <div key={date}>
            <div className="flex items-center gap-2 my-3">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400">{formatDate(dayMessages[0].createdAt)}</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>
            <div className="space-y-2">
              {dayMessages.map((msg) => {
                const isMine = msg.senderId === dbUser?.id;
                const isGeo = msg.body.startsWith('geo:');
                const geoMatch = isGeo ? msg.body.slice(4).split(',') : null;
                const mapsUrl = geoMatch
                  ? `https://www.google.com/maps/dir/?api=1&destination=${geoMatch[0]},${geoMatch[1]}&travelmode=bicycling`
                  : null;
                return (
                  <div
                    key={msg.id}
                    className={`flex items-end gap-2 ${isMine ? 'flex-row-reverse' : 'flex-row'}`}
                  >
                    {!isMine && (
                      <Avatar url={msg.sender.avatarUrl} nickname={msg.sender.nickname} />
                    )}
                    <div
                      className={`max-w-[75%] ${isMine ? 'items-end' : 'items-start'} flex flex-col gap-0.5`}
                    >
                      {isGeo && mapsUrl ? (
                        <a
                          href={mapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`flex flex-col gap-1 px-3 py-2.5 rounded-2xl text-sm ${
                            isMine
                              ? 'bg-blue-600 text-white rounded-br-sm'
                              : 'bg-gray-100 text-gray-900 rounded-bl-sm'
                          }`}
                        >
                          <span className="flex items-center gap-1.5 font-medium">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                            </svg>
                            いまここにいます
                          </span>
                          <span className={`text-xs ${isMine ? 'text-blue-100' : 'text-gray-500'}`}>
                            Google マップで開く →
                          </span>
                        </a>
                      ) : (
                        <div
                          className={`px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
                            isMine
                              ? 'bg-blue-600 text-white rounded-br-sm'
                              : 'bg-gray-100 text-gray-900 rounded-bl-sm'
                          }`}
                        >
                          {msg.body}
                        </div>
                      )}
                      <span className="text-xs text-gray-400">{formatTime(msg.createdAt)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {messages.length === 0 && !isNewConversation && (
          <p className="text-center text-sm text-gray-400 py-10">まだメッセージはありません</p>
        )}
        <div ref={bottomRef} />
      </div>

      {/* 送信フォーム */}
      <div className="border-t border-gray-100 bg-white px-4 py-3 space-y-2">
        <div className="flex items-end gap-2">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="メッセージを入力..."
            maxLength={1000}
            rows={1}
            className="flex-1 resize-none rounded-2xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400 max-h-32 overflow-y-auto"
            style={{ minHeight: '42px' }}
          />
          <button
            onClick={() => void handleSend()}
            disabled={!body.trim() || sending}
            className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-blue-600 text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors"
            aria-label="送信"
          >
            {sending ? (
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M22 2L11 13"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M22 2L15 22 11 13 2 9l20-7z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
        </div>
        {body.length > 900 && (
          <p className="text-xs text-gray-400 text-right">{body.length}/1000</p>
        )}
        <div className="flex items-center justify-between">
          <button
            onClick={handleSendLocation}
            disabled={sending}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors py-1"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
            </svg>
            現在地を送る
          </button>
        </div>
      </div>
    </div>
  );
}
